/** Persistent anonymous identity, reused across reloads.
 *
 *  Persists through BOTH localStorage and a first-party cookie. On a top-level
 *  page (e.g. a standalone hosted widget at relay.example.com) the cookie
 *  survives even if localStorage is unavailable or cleared, so a guest keeps
 *  the same id across refreshes — which is what lets the server resolve their
 *  existing conversation and load history. (In a cross-origin iframe both may
 *  be partitioned; pass an explicit `userId`/`token` for that case.)
 */
const KEY = 'oc_uid'

function readCookie(name: string): string | null {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    return m ? decodeURIComponent(m[1]!) : null
  } catch { return null }
}

function writeCookie(name: string, value: string): void {
  try {
    const maxAge = 60 * 60 * 24 * 365 // 1 year
    const secure = location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`
  } catch { /* cookies disabled — best effort */ }
}

function newId(): string {
  return `g_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
}

export function persistentUid(): string {
  let existing: string | null = null
  try { existing = localStorage.getItem(KEY) } catch { /* unavailable */ }
  if (!existing) existing = readCookie(KEY)

  const id = existing ?? newId()

  // Write through to both stores so whichever is available carries it forward.
  try { localStorage.setItem(KEY, id) } catch { /* unavailable */ }
  writeCookie(KEY, id)

  return id
}
